import Vue from 'vue';
import Raven from 'raven-js';
import store from 'store';
import Http from 'services/Http';
import Api from 'services/Api';
import router from '../router';
import { get } from 'lodash';
import VueI18n from 'services/i18n';
import ApiError from 'utils/errors/ApiError';

const grantTypes = {
  code: 'code',
  refresh_token: 'refresh_token',
};

function setHttpContext(userId, userType, token, refreshToken) {
  Http.setToken(token);
  Http.setRefreshToken(refreshToken);
  Http.setUserType(userType);
  Raven.setUserContext(userId);
}

const Auth = {
  /**
   * Login user with email and password
   *
   * @param {string} email      The user's email
   * @param {string} password   The user's password
   */
  login(email, password, rememberMe) {
    console.log('Logging in...');
    return Api.post('auth/local', { username: email, password })
      .then(
        res => this.authorize(res.data.authorization_code),
        err => {
          const message = get(err, 'body.message');

          if (message) {
            throw new ApiError(message);
          }

          if (err.status === 401) {
            throw new Error(VueI18n.t('login_form.errors.invalid_credentials'));
          }

          if (err.status === 403) {
            throw new Error(VueI18n.t('login_form.errors.not_confirmed'));
          }

          throw new Error(VueI18n.t('login_form.errors.login_failed'));
        }
      )
      .then(
        data => {
          const { userId, token, refreshToken } = data;

          rememberMe
            ? setHttpContext({ userId }, 'journeys', token, refreshToken)
            : setHttpContext({ userId }, 'journeys', token);

          return store.dispatch('account/init', userId);
        },
        err => {
          if (err instanceof Error) {
            throw err;
          }

          throw new Error(
            VueI18n.t('login_form.errors.retrieving_token_failed')
          );
        }
      );
  },

  /**
   * Login to the main Api with an auth code
   *
   * @param   {string} code  A 128 character key ,e.g. "c31f17ded6cd66f6e0f7437bac76834a42fffaf391ef8a43db91811779f01bd98ecab22d4c8bfc3cd251a19b4c5e4f14b8d416538e04a296a1cbec0c046e9be2"
   * @param   {String} [grantType=code] grant type used for token retrieval
   * @returns {Promise}
   */
  authorize(code, grantType = grantTypes.code) {
    const data = {
      [grantType]: code,
      grant_type: grantType,
      token_type: 'manage',
    };

    return Api.post('auth/token', data).then(res => {
      return {
        userId: res.body.user_id,
        token: res.body.token,
        refreshToken: res.body.refresh_token,
      };
    });
  },

  /**
   * Log in again if user is already logged in
   */
  async refresh() {
    const userType = Vue.localStorage.get('userType');
    const prevRefreshToken = Vue.localStorage.get('refreshToken');

    if (!prevRefreshToken || userType === 'business') {
      const prevToken = Vue.localStorage.get('token');
      const userId = Vue.localStorage.get('userId');

      if (!prevToken || !userId) {
        return Promise.resolve(false);
      }

      console.log('Logging back in with token...');
      setHttpContext({ userId }, userType || 'journeys', prevToken);
      await store.dispatch('account/init', userId);

      return Promise.resolve(true);
    }

    const { userId, token, refreshToken } = await this.authorize(
      prevRefreshToken,
      grantTypes.refresh_token
    );

    console.log('Logging back in with refresh token...');
    setHttpContext({ userId }, 'journeys', token, refreshToken);
    await store.dispatch('account/init', userId);

    return Promise.resolve(true);
  },

  /**
   * Log the user out
   */
  logout() {
    console.log('Logging out...');

    setHttpContext();

    // reset all modules
    const modules = store._modulesNamespaceMap;
    Object.keys(modules).forEach(key => {
      const module = modules[key]._rawModule;
      if (module.mutations && module.mutations.reset) {
        store.commit(key + 'reset');
      }
    });

    // return home
    router.push({ path: '/login' });
  },

  register(
    firstname,
    lastname,
    company,
    region,
    username,
    password,
    projectCode,
    newsletter
  ) {
    console.log('Registering...');

    const data = {
      firstname,
      lastname,
      company,
      region,
      username,
      password,
      project_code:
        projectCode && projectCode.length > 0 ? projectCode : undefined,
      auth_method: 'password',
      interest: 'journeys',
      privacy_policy: 'default',
      privacy_policy_at: new Date().toISOString(),
      subscribe_to_newsletter: newsletter,
    };

    return Api.post('users', data).then(
      res => res,
      err => {
        const message = get(err, 'body.message') || get(err, 'body');

        if (message) {
          throw new Error(message);
        }

        throw new Error(
          err ? err.message : 'register_form.errors.register_failed'
        );
      }
    );
  },

  resetPasswordRequest(email) {
    return Api.post('auth/reset-password/request', { email }).then(
      res => res,
      err => {
        const message = get(err, 'body.message') || get(err, 'body');

        if (message) {
          throw new Error(message);
        }

        throw new Error(
          err
            ? err.message
            : 'reset_password_form.errors.reset_password_request_failed'
        );
      }
    );
  },

  resetPassword(password, resetCode) {
    return Api.post('auth/reset-password/reset', {
      password,
      reset_code: resetCode,
    }).then(
      res => res,
      err => {
        const message = get(err, 'body.message') || get(err, 'body');

        if (message) {
          throw new Error(message);
        }

        throw new Error(
          err ? err.message : 'reset_password_form.errors.reset_password_failed'
        );
      }
    );
  },

  resendConfirmation(email) {
    return Api.post('auth/confirmation/resend', { email }).then(
      res => res,
      err => {
        const message = get(err, 'body.message') || get(err, 'body');

        if (message) {
          throw new Error(message);
        }

        throw new Error(
          err ? err.message : 'register_form.errors.confirm_failed'
        );
      }
    );
  },

  acceptInvitation(code) {
    return Api.post(`v2/business_users/invite_confirmations`, { code });
  },

  confirmMfaReset(code) {
    return Api.post(`v2/business_users/mfa_reset_confirmations`, { code });
  },

  confirmEmail(confirmation_code) {
    return Api.post('users/confirm', { confirmation_code })
      .then(
        res => {
          const { authorization_code, message } = res.body;
          if (authorization_code) {
            return this.authorize(authorization_code);
          }
          if (message) {
            throw new Error(message);
          }
        },
        err => {
          const message = get(err, 'body.message') || get(err, 'body');

          if (message) {
            throw new Error(message);
          }

          throw new Error(
            err ? err.message : 'email_confirmation.errors.confirmation_failed'
          );
        }
      )
      .then(
        data => {
          const { userId, token } = data;

          setHttpContext({ userId }, 'journeys', token);

          return store.dispatch('account/init', userId);
        },
        err => {
          if (err instanceof Error) {
            throw err;
          }

          throw new Error(
            VueI18n.t('login_form.errors.retrieving_token_failed')
          );
        }
      );
  },
};

window.Auth = Auth;

export default Auth;
